import { useRef, useState, type KeyboardEvent } from 'react';
import { PaperPlaneRight } from '@phosphor-icons/react';
import './ChatWindow.css';

interface MessageInputProps {
  onSendMessage: (message: string) => void;
  disabled?: boolean;
}

function MessageInput({ onSendMessage, disabled = false }: MessageInputProps) {
  const [message, setMessage] = useState('');
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const autoSize = () => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 120) + 'px';
  };

  const handleSend = () => {
    if (message.trim() && !disabled) {
      onSendMessage(message.trim());
      setMessage('');
      // Shrink back to one line once the text is cleared.
      requestAnimationFrame(autoSize);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // Ignore Enter while an IME composition is in progress.
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="message-input-container">
      <textarea
        ref={inputRef}
        id="chat-message"
        name="message"
        className="message-input"
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        onKeyDown={handleKeyDown}
        onInput={autoSize}
        placeholder={disabled ? 'Waiting for a reply…' : 'Type your message…'}
        aria-label="Message"
        disabled={disabled}
        rows={1}
      />
      <button
        className="send-button"
        onClick={handleSend}
        disabled={disabled || !message.trim()}
        aria-label="Send message"
      >
        <PaperPlaneRight size={18} weight="fill" aria-hidden="true" />
      </button>
    </div>
  );
}

export default MessageInput;
